// The split login page — Signet's, with this application's words and its own plate.
//
// Pre-auth and outside the app shell, so it is its own visual world (styles:
// public/styles/main.css, section "LOGIN — the split page", everything under `.lg-`). A
// full-bleed plate ("Ein deutsches Märchen") carries the NAVEN mark, the wordmark and the
// tagline; the paper column carries the sign-in.
//
// Behaviour replicates the kit's LoginPage (lib/auth-sso-kit — untouchable):
//   · `authMethod === 'oidc'` → a single SSO button; otherwise the e-mail + password form
//   · `#error=not_authorized | oidc_failed | no_email` becomes a message once, hash cleared
//   · empty e-mail / password are rejected before calling `login`
// Registration is `allowlist`: SEED_OPERATOR_EMAIL becomes admin on first login. There is no
// self-service password reset in the CRM (Signet has one); the foot says who to ask instead.
//
// LANGUAGE: pre-auth there is no operator preference and no LocaleProvider above this page in
// the tree, so the strings follow the BROWSER through this page's own catalogues
// (`LoginPage.de.ts`, `LoginPage.fr.ts`), like every other page. English text IS the key.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';

import { isUiLang, makeT, type Dict, type UiLang } from '../../shared/i18n.js';

import { de } from './LoginPage.de.js';
import { fr } from './LoginPage.fr.js';

const CATALOGS: Record<UiLang, Dict | null> = { en: null, de, fr };

const browserLang = (): UiLang => {
  const l = (typeof navigator !== 'undefined' ? navigator.language : 'en').slice(0, 2).toLowerCase();
  return isUiLang(l) ? l : 'en';
};

/* 1×1 transparent GIF: the <img> fallback below 641px, where the panel drops the plate —
   phones never download the 490 KB webp. */
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export default function LoginPage() {
  const t = useMemo(() => makeT(CATALOGS[browserLang()]), []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithOIDC, authMethod } = useAuth();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('error=not_authorized')) setError(t('Your email is not authorized to access this application. Contact an administrator.'));
    else if (hash.includes('error=oidc_failed')) setError(t('Authentication failed. Please try again.'));
    else if (hash.includes('error=no_email')) setError(t('No email was received from the identity provider.'));
    if (hash.includes('error=')) window.location.hash = '';
  }, [t]);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError(t('Email is required')); return; }
    if (!password) { setError(t('Password is required')); return; }
    setLoading(true);
    setError('');
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t('Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="lg-root">
      <aside className="lg-brand" aria-label="CRM">
        <picture className="lg-brand-media">
          <source media="(min-width: 641px)" srcSet="/img/login-maerchen.webp" type="image/webp" />
          <img src={BLANK} alt="" width={1742} height={2560} decoding="async" fetchPriority="high" />
        </picture>
        <div className="lg-scrim" aria-hidden="true" />
        <a className="lg-logo" href="https://naven.ch" aria-label="naven">NAVEN<i aria-hidden="true" /></a>

        <div className="lg-brand-stack">
          <p className="lg-wordmark lg-enter lg-d1">crm<i aria-hidden="true" /></p>
        </div>
      </aside>

      <section className="lg-form" aria-labelledby="lg-heading">
        <div className="lg-card lg-enter lg-d1">
          <h1 id="lg-heading">{t('Sign in')}</h1>
          <p className="lg-lede">{t('Access to the relationship record')}</p>

          {error && (
            <div className="lg-error" role="alert">
              {error}
            </div>
          )}

          {authMethod === 'oidc' ? (
            <button type="button" className="lg-btn" onClick={loginWithOIDC}>
              {t('Log in with SSO')}
              <Arrow />
            </button>
          ) : (
            <form onSubmit={handlePasswordSubmit} noValidate>
              <div className="lg-field">
                <label className="lg-label" htmlFor="email">{t('Email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="lg-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('your@email.ch')}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="lg-field">
                <label className="lg-label" htmlFor="password">{t('Password')}</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className="lg-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('Enter your password')}
                  autoComplete="current-password"
                />
              </div>

              <button type="submit" className="lg-btn" disabled={loading}>
                {loading ? t('Signing in…') : t('Sign in')}
                {!loading && <Arrow />}
              </button>
            </form>
          )}

          <p className="lg-foot">{t('Only pre-approved email addresses can log in.')}</p>
        </div>
      </section>
    </main>
  );
}

function Arrow() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}
